import {
  useAuthoredRows,
  useCreateAuthoredRow,
  useDeleteAuthoredRow,
  useUpdateAuthoredRow,
} from 'components/IAP/hooks/icsAuthoredFormHooks';
import { useRecordFormVersionSave } from 'components/IAP/hooks/icsFormVersionHooks';
import { useICSContext } from 'components/IAP/planningView/ICSContext';

import { Ics204CardSection } from '../AssignmentList/ics204BoxLabels';
import SaveableField from '../SaveableField';
import {
  ICS232_BOX_LABELS,
  ICS232_CATEGORIES,
  ICS232_SITE_COLUMNS,
} from '../ics232BoxLabels';
import {
  BoxRowTable,
  NewCgFormCard,
  NewCgHeaderBoxes,
  NewCgPreparedByBox,
} from './newCgFormBoxes';

/**
 * ICS 232-CG, boxed as the sheet prints it.
 *
 * Boxes 4 and 5 are the same site table twice, split by the `category` stored
 * on each row, each closing with its own Narrative blank.
 */
const Ics232Card = () => {
  const { effectivePeriodNumber } = useICSContext();
  const { data: summaries = [] } = useAuthoredRows('rarSummaries');
  const { data: sites = [] } = useAuthoredRows('rarSites');
  const createSummary = useCreateAuthoredRow('rarSummaries');
  const updateSummary = useUpdateAuthoredRow('rarSummaries');
  const createSite = useCreateAuthoredRow('rarSites');
  const updateSite = useUpdateAuthoredRow('rarSites');
  const deleteSite = useDeleteAuthoredRow('rarSites');
  const recordVersionSave = useRecordFormVersionSave(
    'ICS_232',
    effectivePeriodNumber
  );
  const onSuccess = () => recordVersionSave.mutate();

  const summary = summaries[0] ?? null;

  const saveNarrative = (patch: Record<string, string>) => {
    if (!summary) {
      createSummary.mutate(patch, { onSuccess });
      return;
    }
    updateSummary.mutate(
      { entityId: String(summary.summary_id), ...patch },
      { onSuccess }
    );
  };

  const sitesIn = (category: string) =>
    sites.filter((site) => String(site.category ?? '') === category);

  const saveSite = (entityId: string, patch: Record<string, string>) =>
    updateSite.mutate({ entityId, ...patch }, { onSuccess });

  const siteBox = (
    category: string,
    boxLabel: string,
    testId: string,
    narrativeKey: 'environmental_narrative' | 'socioeconomic_narrative'
  ) => (
    <Ics204CardSection boxLabel={boxLabel} testId={testId} className="tw:mt-0">
      <BoxRowTable
        columns={ICS232_SITE_COLUMNS}
        rows={sitesIn(category)}
        identityColumn="site_id"
        testId={`${testId}-table`}
        addLabel="Add Site"
        onSave={saveSite}
        onDelete={(entityId) => deleteSite.mutate(entityId, { onSuccess })}
        onAdd={async () => {
          const created = await createSite.mutateAsync({ category });
          onSuccess();
          return String(created?.site_id ?? '');
        }}
      />
      <div className="tw:mt-3">
        <SaveableField
          id={`${testId}-narrative`}
          label="Narrative"
          multiline
          placeholder="Enter narrative..."
          value={String(summary?.[narrativeKey] ?? '')}
          onSave={(value) => saveNarrative({ [narrativeKey]: value })}
        />
      </div>
    </Ics204CardSection>
  );

  return (
    <NewCgFormCard
      title="Resources at Risk Summary ICS-232"
      formname="ICS_232_CG"
      baseId="ICS_232"
      exportLabel="Preview ICS-232"
    >
      <NewCgHeaderBoxes labels={ICS232_BOX_LABELS} testIdPrefix="ics232" />

      {siteBox(
        ICS232_CATEGORIES.environmental,
        ICS232_BOX_LABELS.environmental,
        'ics232-section-environmental',
        'environmental_narrative'
      )}

      {siteBox(
        ICS232_CATEGORIES.socioeconomic,
        ICS232_BOX_LABELS.socioeconomic,
        'ics232-section-socioeconomic',
        'socioeconomic_narrative'
      )}

      <NewCgPreparedByBox
        baseId="ICS_232"
        boxLabel={ICS232_BOX_LABELS.preparedBy}
        testIdPrefix="ics232"
      />
    </NewCgFormCard>
  );
};

export default Ics232Card;
